import React, { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { EmptyState, Skeleton } from "@/components/EmptyState";
import { PackageMinus, ScanLine } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";

const REASONS = ["usage", "transfer", "damage", "sale", "adjustment", "other"];

export default function GoodsOuttake() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ item_id: "", quantity: 1, reason: "usage", note: "" });
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, mv] = await Promise.all([
        api.get("/inventory?limit=2000"),
        api.get("/goods/movements?type=outtake&limit=20"),
      ]);
      setItems(inv.data.items || []);
      setRecent(mv.data.movements || []);
    } catch (e) { toast.error(extractErr(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.item_id) { toast.error("Select an item"); return; }
    if (Number(form.quantity) <= 0) { toast.error("Quantity must be > 0"); return; }
    setSubmitting(true);
    try {
      await api.post("/goods/outtake", { ...form, quantity: Number(form.quantity) });
      toast.success("Stock removed");
      setForm({ item_id: "", quantity: 1, reason: "usage", note: "" });
      refresh();
    } catch (e) { toast.error(extractErr(e)); }
    setSubmitting(false);
  };

  return (
    <AppShell title="Goods Outtake" subtitle="Remove stock with reason & notes"
      headerActions={<Button variant="secondary" size="sm" onClick={() => navigate("/app/scanner")}><ScanLine className="w-4 h-4 mr-1.5" />Scan</Button>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-4 sm:p-6" data-testid="goods-outtake-form">
          <h3 className="font-heading text-lg font-semibold">Remove stock</h3>
          <p className="text-sm text-muted-foreground">Records an outtake and decreases on-hand quantity.</p>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Item *</Label>
              <Select value={form.item_id} onValueChange={(v) => setForm((p) => ({ ...p, item_id: v }))}>
                <SelectTrigger data-testid="outtake-item-select"><SelectValue placeholder="Select inventory item" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {items.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      <span className="font-mono text-xs mr-2">{i.sku}</span>{i.name} <span className="text-muted-foreground">— {i.quantity} {i.unit}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Quantity *</Label>
              <Input type="number" step="any" min="0" value={form.quantity} onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))} data-testid="outtake-qty-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm((p) => ({ ...p, reason: v }))}>
                <SelectTrigger data-testid="outtake-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Note</Label>
              <Textarea value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} rows={2} />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t border-border mt-2">
              <Button type="submit" disabled={submitting} data-testid="outtake-submit-button"><PackageMinus className="w-4 h-4 mr-1.5" />{submitting ? "Saving…" : "Record outtake"}</Button>
            </div>
          </form>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4">
          <h3 className="font-heading font-semibold">Recent outtakes</h3>
          <div className="mt-3 space-y-2 max-h-[560px] overflow-y-auto">
            {loading ? [...Array(5)].map((_, i) => <Skeleton key={i} className="h-14" />)
              : recent.length === 0 ? <EmptyState className="py-6 border-0" title="No recent outtakes" description="Outtakes will appear here." />
              : recent.map((m) => (
                  <div key={m.id} className="rounded-lg border border-border p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="font-medium truncate">{m.item_name}</div>
                      <div className="text-amber-600 dark:text-amber-400 font-medium">-{m.quantity}</div>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span className="font-mono">{m.item_sku}</span>
                      <span>{new Date(m.timestamp).toLocaleString()}</span>
                    </div>
                    {m.reason && <div className="text-[11px] text-muted-foreground mt-1 capitalize">Reason: {m.reason}</div>}
                  </div>
                ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
